import { apiClient, type PaginatedResponse } from './client'
import LogTool from '../logger/logTools'
import { GenericFilterType, PaginatedQueryParameters, SearchOptions } from "./hooks/utils/types";
import { toSnakeCase } from "../utils/functions";

const log = new LogTool({ context: 'Pagination', enable: true, logLevel: 'warn' })

/**
 * Builds a valid backend API URL from the given (base) url and query parameters.
 * This can be used when fetching data, as well as using the search endpoints.
 *
 * Important note:
 *   When using this function to build an API url for a search endpoint, make sure
 *   to only provide the filters property in the parameter object.
 *
 * @param url string - The base URL to build the API URL upon.
 * @param parameters PaginatedQueryParameters - The parameters to include in the API URL.
 *  Using these parameters, you can expand properties on fetched instances, or
 *  sort the results by a specific property, or ...
 * @param instances number[] - Optional list of IDs/PKs to include in the API URL.
 *  Using this parameter, you can carry out a batch request to fetch multiple,
 *  specific instances at once.
 */
function buildApiUrl<TEntity, TFilters extends GenericFilterType>(
  url: string,
  parameters?: PaginatedQueryParameters<TEntity, TFilters>,
  instances?: number[]
): string
{
  const baseUrl = process.env.REACT_APP_API_URL
  if (!baseUrl) {
    throw new Error('REACT_APP_API_URL is not defined. Please set the environment variable.')
  }

  // Use URL constructor to properly build query parameters
  // We construct a full URL temporarily to use URL.searchParams, then extract just the path+search
  // so apiClient can prepend its own base URL
  const apiUrl = url.startsWith('http://') || url.startsWith('https://')
    ? new URL(url)
    : new URL(url, baseUrl.endsWith('/') ? baseUrl : baseUrl + '/')

  // Build query parameters using the URL object
  if(parameters) {
    if(Object.hasOwn(parameters, 'filters')&& parameters.filters !== undefined) {
      const filters = parameters.filters
      // Add parameter q (required for most searches)
      if (filters.q) {
        apiUrl.searchParams.append('q', filters.q)
      }
      // Add all other filter parameters
      Object.entries(filters).forEach(([key, value]) => {
        if (key !== 'q' && value !== undefined && value !== '' && value !== null) {
          if (Array.isArray(value)) {
            apiUrl.searchParams.append(key, value.join(','))
          } else {
            apiUrl.searchParams.append(key, String(value))
          }
        }
      })
    }
    if(Object.hasOwn(parameters, 'expand')
      && parameters.expand
      && parameters.expand.length > 0)
    {
      apiUrl.searchParams.append(
        'expand',
        parameters.expand.join(',')
      )
    }if(Object.hasOwn(parameters, 'only')
      && parameters.only
      && parameters.only.length > 0)
    {
      apiUrl.searchParams.append(
        'only',
        parameters.only.join(',')
      )
    }
    if(Object.hasOwn(parameters, 'sort') && parameters.sort !== undefined) {
      let orderBy= toSnakeCase(parameters.sort.property as string)
      if(parameters.sort.order === 'DESC') {
        // prepend '-' to the sort parameter to indicate the backend
        // that this parameter has to be understood as a descending
        // sort parameter
        orderBy = '-' + orderBy
      }
      apiUrl.searchParams.append(
        'sort',
        orderBy
      )
    }
    if(Object.hasOwn(parameters, 'page') && parameters.page !== undefined) {
      apiUrl.searchParams.append(
        'page',
        parameters.page.toString()
      )
    }
    if(Object.hasOwn(parameters, 'pageSize') && parameters.pageSize !== undefined) {
      apiUrl.searchParams.append(
        'page_size',
        parameters.pageSize.toString()
      )
    }
  }
  if(Array.isArray(instances) && instances.length > 0) {
    apiUrl.searchParams.append(
      'instances',
      instances.join(',')
    )
  }

  // Return relative URL (pathname + search) so apiClient can prepend its base URL
  // If the original URL was already absolute, return it as-is (apiClient.buildURL will handle it)
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return apiUrl.toString()
  }

  // Extract pathname and search params as a relative URL
  return apiUrl.pathname + apiUrl.search
}

export default buildApiUrl

/**
 * Build URL with query parameters
 */
export function buildFetchUrl(baseUrl: string, parameters: string[]): string {
  if (parameters.length === 0) {
    return baseUrl
  }

  const separator = baseUrl.includes('?') ? '&' : '?'
  return baseUrl + separator + parameters.join('&')
}

/**
 * Fetch a single page from a paginated endpoint
 */
export async function fetchPage<T>(
  url: string,
  parameters: string[] = []
): Promise<PaginatedResponse<T>> {
  const fetchUrl = buildFetchUrl(url, parameters)
  log.debug('Fetching page:', fetchUrl)
  
  const response = await apiClient.get<PaginatedResponse<T>>(fetchUrl)
  return response
}

/**
 * Fetch all pages from a paginated endpoint (use with caution for large datasets)
 * This is kept for backward compatibility but should be used sparingly
 * 
 * @deprecated Consider using server-side pagination with fetchPage instead
 */
export async function fetchAllPages<T>(
  url: string,
  parameters: string[] = []
): Promise<{ results: T[]; count: number }> {
  let allResults: T[] = []
  let nextUrl: string | null = url
  let totalCount = 0

  log.info('Fetching all pages from:', url)

  while (nextUrl) {
    const response: PaginatedResponse<T> = await fetchPage<T>(nextUrl, nextUrl === url ? parameters : [])
    
    allResults = [...allResults, ...response.results]
    totalCount = response.count
    nextUrl = response.next

    log.debug(`Fetched ${response.results.length} items, total so far: ${allResults.length}`)
  }

  log.info(`Completed fetching all pages. Total items: ${allResults.length}`)

  return {
    results: allResults,
    count: totalCount,
  }
}

/**
 * Parse page number from pagination URL
 */
export function getPageFromUrl(url: string | null): number | undefined {
  if (!url) {
    return undefined
  }

  try {
    const urlObj = new URL(url)
    const page = urlObj.searchParams.get('page')
    return page ? parseInt(page, 10) : undefined
  } catch {
    return undefined
  }
}

/**
 * Build pagination parameters for API requests
 */
export interface PaginationOptions {
  page?: number
  pageSize?: number
  expand?: string[]
  filters?: Record<string, any>
}

export function buildPaginationParams(options: PaginationOptions): string[] {
  const params: string[] = []

  if (options.page !== undefined) {
    params.push(`page=${options.page}`)
  }

  if (options.pageSize !== undefined) {
    params.push(`page_size=${options.pageSize}`)
  }

  if (options.expand && options.expand.length > 0) {
    params.push(`expand=${options.expand.join(',')}`)
  }

  // Add filter parameters
  if (options.filters) {
    Object.entries(options.filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (key === 'exclude') {
          const expandSet = new Set(options.expand ?? [])
          const values = Array.isArray(value) ? value : String(value).split(',')
          const sanitized = values
            .map((token) => token?.toString().trim())
            .filter((token): token is string => Boolean(token))
            // Avoid excluding a field that we explicitly expand (causes DRF errors)
            .filter((token) => !expandSet.has(token.split('__')[0]))

          if (sanitized.length === 0) {
            return
          }

          if (Array.isArray(value)) {
            sanitized.forEach((token) => params.push(`exclude=${encodeURIComponent(token)}`))
          } else {
            params.push(`exclude=${encodeURIComponent(sanitized.join(','))}`)
          }
          return
        }

        if (Array.isArray(value)) {
          // Handle array filters
          value.forEach(v => params.push(`${key}=${encodeURIComponent(v)}`))
        } else {
          params.push(`${key}=${encodeURIComponent(value)}`)
        }
      }
    })
  }

  return params
}
